// ============================================================
// MOONSHINE MASTERS — missions.js        THE ENDLESS BOARD (pure)
//
// Turns the grand design (missionDesign.js numbers + data/text.json words + toonzCast.js people) into Tome Contracts.
// Three kinds of contract live side by side, all through the same ACCEPT / DELIVER:
//   · the STORY five/six (moonshineData CONTRACTS c1..c6)   - fixed, in order
//   · the EASTER EGGS (missionDesign EGGS, ids x_*)          - fixed, once per save, shown OPEN or appear on a trigger
//   · the ENDLESS ones (ids "g.<seat>.<n>")                  - made on demand from (seat, n); never stored, so never run out
// A generated contract is NEVER saved as an object: the save holds only its id (while accepted), and contractOf(id) rebuilds the
// identical contract on any screen. That is why co-op needs no extra wire and why a design edit never corrupts a save.
//
// No React, no three, no Date: dependency-light so tools/rules.check.mjs can play it headlessly.
// ============================================================
import { CONTRACTS, RECIPES, PLACES, QUALITY, FLAVORS } from "./moonshineData";
import { TIERS, DESIGN_SEED, SLOTS_PER_TIER, UNLOCK_LEGENDARY_AFTER, UNLOCK_MYTHICAL_AFTER, MYTHICAL_NEEDS_SPECS, RAMP, TIER_CFG, HINT_CHANCE, EGGS, EGG_AT, EGG_PAYS, PROOF_ASKS, STARS } from "./missionDesign";
import { TOONZ } from "./toonzCast";
import { say, listOf } from "./text";

// ── seeds: a string -> numbers that never change (FNV-1a + mulberry32) ──
const hashStr = (str) => { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
const mulberry = (seed) => () => { seed = (seed + 0x6D2B79F5) >>> 0; let t = seed; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };

// ── seats ──
const LETTER = { fable: "f", legendary: "l", mythical: "m" }, TIER_OF = { f: "fable", l: "legendary", m: "mythical" };
export const seatsOf = (tier) => Array.from({ length: Math.max(0, Math.floor(SLOTS_PER_TIER[tier] || 0)) }, (_, i) => `${LETTER[tier]}${i + 1}`);
export const ALL_SEATS = TIERS.flatMap(seatsOf);
export const genId = (seat, n) => `g.${seat}.${n}`;
const GEN_RE = /^g\.([flm])([1-9])\.(0|[1-9]\d{0,4})$/;
export const parseGen = (id) => { const m = typeof id === "string" && GEN_RE.exec(id); if (!m) return null; const seat = m[1] + m[2]; return seatsOf(TIER_OF[m[1]]).includes(seat) ? { seat, tier: TIER_OF[m[1]], n: +m[3] } : null; };
export const isGenId = (id) => !!parseGen(id);
export const ramp = (n) => 1 + RAMP.max * (n / (n + Math.max(0.0001, RAMP.half)));

// ── which recipes belong to which tier: derived from where their ingredients grow, so a NEW recipe or plant joins by itself ──
let POOLS = null;
function pools() {
  if (POOLS) return POOLS;
  const ingTier = {};
  for (const p of PLACES) for (const ing of p.yields) ingTier[ing] = Math.max(ingTier[ing] || 0, p.tier || 1);
  const rt = (r) => Math.max(1, ...Object.keys(r.needs).map((k) => ingTier[k] || 1));
  const ids = Object.keys(RECIPES).sort().filter((id) => !RECIPES[id].hidden && !RECIPES[id].free);   // 2026-10-31: the secret recipes and the house blend are never asked for
  POOLS = {
    fable: ids.filter((id) => rt(RECIPES[id]) === 1 && !RECIPES[id].deviance && !RECIPES[id].forageOnly),
    legendary: ids.filter((id) => rt(RECIPES[id]) === 2 && RECIPES[id].forageOnly),
    mythical: ids.filter((id) => rt(RECIPES[id]) === 3 && RECIPES[id].forageOnly),
  };
  return POOLS;
}

// ── the roster, per tier, from the Words page (unknown names dropped; an empty tier falls back to everybody) ──
const rosterOf = (tier) => { const l = listOf(`clients.${tier}`, 1).filter((id) => TOONZ[id]); return l.length ? l : Object.keys(TOONZ); };
export const personOf = (speciesId) => (TOONZ[speciesId] ? { id: speciesId, ...TOONZ[speciesId] } : null);
export const voiceOf = (speciesId) => say(`voice.${speciesId}`);
const eggById = (id) => EGGS.find((e) => e.id === id);
const hintable = () => EGGS.filter((e) => e.mode === "whisper" && TOONZ[e.hintBy] && say(`egg.${e.id}.hint`) !== `egg.${e.id}.hint` && say(`egg.${e.id}.hint`).trim());

// ── how an order is worded ──
function needWords(req) {
  const rec = RECIPES[req.recipe];
  const what = (req.flavor && FLAVORS[req.flavor] ? FLAVORS[req.flavor] + " " : "") + rec.name;
  const q = req.minQ && req.minQ !== "rough" ? " (" + say("need.quality", { q: req.minQ }) + ")" : "";
  const pf = Number.isFinite(req.proof) ? " " + say("need.proof", { p: req.proof }) : "";
  return (req.jars > 1 ? say("need.many", { n: req.jars, what }) : say("need.one", { what })) + pf + q;
}
const clauseOf = (req, vow) => {
  const bits = [req.notRushed && say("clause.norush"), vow && say("clause.vow")].filter(Boolean);
  const t = bits.length ? bits.join(" ") : say("clause.none");
  return { solo: t, coop: t };
};

// ── THE ENDLESS CONTRACT: (seat, n) -> the same contract every time ──
const CACHE = new Map();
function genContract(g) {
  const key = genId(g.seat, g.n);
  if (CACHE.has(key)) return CACHE.get(key);
  const cfg = TIER_CFG[g.tier], pool = pools()[g.tier];
  if (!cfg || !pool || !pool.length) return null;
  const r = mulberry(hashStr(`${DESIGN_SEED}|${g.seat}|${g.n}`));
  // every roll is drawn in a fixed order, used or not, so one dial changing never reshuffles the OTHER choices of a mission
  const rClient = r(), rRecipe = r(), rJars = r(), rQual = r(), rFlavor = r(), rFlavorKind = r(), rNoRush = r(), rVow = r(), rHint = r(), rHintEgg = r(), rAsk = r();
  const rProof = r(), rProofKind = r();   // 2026-10-25: drawn LAST, so every mission dealt before the proof seam is otherwise unchanged
  const rStar = r();                      // 2026-10-31 STARS: drawn after the proof, for the same reason
  const recipe = pool[Math.min(pool.length - 1, Math.floor(rRecipe * pool.length))];
  const jarsTop = Math.min(cfg.jarsCap, cfg.jarsMax + Math.floor(g.n / Math.max(1, cfg.jarsGrowEvery)));
  const jars = cfg.jarsMin + Math.min(Math.max(0, jarsTop - cfg.jarsMin), Math.floor(rJars * (Math.max(0, jarsTop - cfg.jarsMin) + 1)));
  const qr = g.n < cfg.qUpAt ? cfg.qMin : cfg.qMin + Math.min(cfg.qMax - cfg.qMin, Math.floor(rQual * (cfg.qMax - cfg.qMin + 1)));
  const flavorKeys = Object.keys(FLAVORS);
  const flavor = rFlavor < cfg.flavorChance && flavorKeys.length ? flavorKeys[Math.min(flavorKeys.length - 1, Math.floor(rFlavorKind * flavorKeys.length))] : null;
  const notRushed = rNoRush < cfg.norushChance && !RECIPES[recipe].deviance;
  const vow = rVow < cfg.vowChance ? "flawless" : null;
  const req = { jars, recipe, minQ: QUALITY[Math.max(1, Math.min(QUALITY.length - 1, qr))] };
  if (flavor) req.flavor = flavor;
  if (notRushed) req.notRushed = true;
  const proof = rProof < (cfg.proofChance || 0) && PROOF_ASKS.length ? PROOF_ASKS[Math.min(PROOF_ASKS.length - 1, Math.floor(rProofKind * PROOF_ASKS.length))] : null;
  if (proof) req.proof = proof;
  const hot = STARS[g.tier] && rStar < STARS[g.tier].chance ? STARS[g.tier] : null;   // a hot job: stars on the crew, better pay
  const coin = Math.max(1, Math.round((hot ? 1 + (hot.bonus || 0) : 1) * (cfg.base + cfg.perJar * jars) * (1 + cfg.qBonus * (qr - 1)) * (flavor ? 1 + cfg.flavorBonus : 1)
    * (notRushed ? 1 + cfg.norushBonus : 1) * (vow ? 1 + cfg.vowBonus : 1) * (proof ? 1 + (cfg.proofBonus || 0) : 1) * ramp(g.n)));
  // who: the roster for this tier - or, now and then, a visitor with a rumour about an egg you haven't found
  const clients = rosterOf(g.tier), hints = hintable();
  let species = clients[Math.min(clients.length - 1, Math.floor(rClient * clients.length))], hintEgg = null;
  if (hints.length && rHint < HINT_CHANCE) { const e = hints[Math.min(hints.length - 1, Math.floor(rHintEgg * hints.length))]; species = e.hintBy; hintEgg = e.id; }
  const asks = listOf(`ask.${g.tier}`, 1);
  const need = needWords(req);
  const c = {
    id: key, gen: true, seat: g.seat, slot: g.seat, n: g.n, tier: g.tier, species,
    client: TOONZ[species] ? TOONZ[species].name : "A stranger", voice: voiceOf(species), hintEgg,
    ask: (asks.length ? asks[Math.min(asks.length - 1, Math.floor(rAsk * asks.length))] : "{need}").replace("{need}", need),
    need, req, reward: { mash: coin }, stars: hot ? Math.max(0, Math.min(3, hot.stars | 0)) : 0, vow, clause: clauseOf(req, vow),
  };
  CACHE.set(key, c);
  return c;
}

// ── THE EASTER EGGS as contracts ──
const EGG_CACHE = new Map();
function eggContract(e) {
  if (EGG_CACHE.has(e.id)) return EGG_CACHE.get(e.id);
  const p = TOONZ[e.client];
  const c = { id: e.id, egg: true, tier: e.tier || "fable", mode: e.mode, species: e.client, client: p ? p.name : "A stranger", voice: voiceOf(e.client),
    ask: say(`egg.${e.id}.ask`), need: needWords(e.req), req: { ...e.req }, reward: { mash: EGG_PAYS[e.id] || 0 }, stars: e.tier === "mythical" && STARS.mythical && STARS.mythical.chance >= 1 ? STARS.mythical.stars : 0,   // caP'n: a Mythical job is 3 stars every time
    clause: clauseOf(e.req, null) };
  EGG_CACHE.set(e.id, c);
  return c;
}
export const EGG_CONTRACTS = EGGS.map(eggContract);
export const STORY_IDS = CONTRACTS.map((c) => c.id);

// THE ONE LOOKUP: a story contract, an egg, or an endless one - by id. Anything else is null.
export function contractOf(id) {
  if (typeof id !== "string") return null;
  const story = CONTRACTS.find((c) => c.id === id);
  if (story) return story;
  const e = eggById(id);
  if (e) return eggContract(e);
  const g = parseGen(id);
  return g ? genContract(g) : null;
}
export const isGen = (c) => !!(c && c.gen);

// ── the saved bits: THE BOARD (seat -> how many times it has been filled) and THE LEDGER (jobs finished, per tier) ──
export const emptyLedger = () => ({ fable: 0, legendary: 0, mythical: 0 });
export function cleanBoard(o) {
  const out = {};
  for (const seat of ALL_SEATS) { const v = o && o[seat]; if (Number.isFinite(v) && v > 0) out[seat] = Math.min(99998, Math.floor(v)); }
  return out;
}
export function cleanLedger(o) {
  const out = emptyLedger();
  for (const t of TIERS) { const v = o && o[t]; if (Number.isFinite(v) && v > 0) out[t] = Math.min(999999, Math.floor(v)); }
  return out;
}

// ── who is on the board right now ──
// What the CREW has done, not just me (co-op): the keeper's Spectacles and counters, carried in the yard (YARD_KEYS "crew").
// Alone, s.crew is undefined and I read my own. As a guest, s.crew is the keeper's, so the board I see is the board the keeper will accept.
export const crewOf = (s) => ({ specs: !!s.specs, booms: (s.stats && s.stats.booms) || 0, caught: (s.stats && s.stats.caught) || 0, dumped: (s.stats && s.stats.dumped) || 0, malted: (s.stats && s.stats.malted) || 0, sights: (s.stats && s.stats.sights) || 0 });
export function cleanCrew(o) {
  const n = (x) => (Number.isFinite(x) && x > 0 ? Math.min(999999, Math.floor(x)) : 0);
  return { specs: !!(o && o.specs === true), booms: n(o && o.booms), caught: n(o && o.caught), dumped: n(o && o.dumped), malted: n(o && o.malted), sights: n(o && o.sights) };
}
const gate = (s) => s.crew || crewOf(s);
// a story contract whose vow you broke comes back (caP'n 2026-10-30) once the crew has finished `reoffer` more jobs (s.vowBack = the job count it waits for)
export const jobsDone = (s) => { const l = s.ledger || {}; return (l.fable || 0) + (l.legendary || 0) + (l.mythical || 0); };
export const storyBack = (s, c) => !!c.reoffer && (s.contracts || {})[c.id] === "broken" && jobsDone(s) >= ((s.vowBack && s.vowBack[c.id]) || 0);
export const storyOpen = (s) => s.contracts.c1 === "done";                       // the tutorial's job done: the town starts coming by
export function tierOpen(s, tier) {
  if (!storyOpen(s)) return false;
  const l = s.ledger || emptyLedger();
  if (tier === "fable") return true;
  if (tier === "legendary") return l.fable >= UNLOCK_LEGENDARY_AFTER;
  if (tier === "mythical") return l.legendary >= UNLOCK_MYTHICAL_AFTER && (!MYTHICAL_NEEDS_SPECS || !!gate(s).specs);
  return false;
}
// what a closed tier is waiting for, in the board's own words (null when open)
export function tierWhy(s, tier) {
  if (tierOpen(s, tier) || !storyOpen(s)) return null;
  const l = s.ledger || emptyLedger();
  if (tier === "legendary") return say("board.locked.legendary", { n: Math.max(0, UNLOCK_LEGENDARY_AFTER - l.fable) });
  if (tier === "mythical") {
    const more = Math.max(0, UNLOCK_MYTHICAL_AFTER - l.legendary);
    return MYTHICAL_NEEDS_SPECS && !gate(s).specs && more === 0 ? say("board.locked.mythical.specs") : say("board.locked.mythical", { n: more });
  }
  return null;
}
// is this egg on the board? (open ones from the first job; whisper ones once you've done the optional thing)
export function eggOpen(s, id) {
  const e = eggById(id);
  if (!e || !storyOpen(s)) return false;
  if (e.mode === "open") return true;
  const g = gate(s);
  if (e.at.flag) return !!g[e.at.flag];
  return (g[e.at.stat] || 0) >= (EGG_AT[e.id] || 1);
}
export const eggHinted = (s, id) => !eggOpen(s, id);
// every contract waiting for a taker, story first, then eggs, then the endless ones by tier
export function offeredList(s) {
  const book = s.contracts || {};
  const story = CONTRACTS.filter((c) => (!book[c.id] || storyBack(s, c)) && (!c.after || book[c.after] === "done"));
  const eggs = EGGS.filter((e) => !book[e.id] && eggOpen(s, e.id)).map(eggContract);
  const gens = [];
  for (const tier of TIERS) {
    if (!tierOpen(s, tier)) continue;
    for (const seat of seatsOf(tier)) {
      const id = genId(seat, (s.board && s.board[seat]) || 0);
      if (book[id]) continue;
      const c = contractOf(id);
      if (c) gens.push(c);
    }
  }
  return [...story, ...eggs, ...gens];
}
export function acceptedList(s) {
  const ids = Object.entries(s.contracts || {}).filter(([, st]) => st === "accepted").map(([id]) => id);
  const rank = (c) => (c.gen ? 2000 + TIERS.indexOf(c.tier) * 100 + c.n : c.egg ? 1000 : CONTRACTS.findIndex((x) => x.id === c.id));
  return ids.map(contractOf).filter(Boolean).sort((a, b) => rank(a) - rank(b));
}
// the seat's next number after a contract of it is settled (finished, vow-broken, or sent away)
export const nextBoard = (s, c) => ({ ...(s.board || {}), [c.seat]: Math.max((s.board && s.board[c.seat]) || 0, c.n) + 1 });
